import { TeamMode } from '@/services/utils/enums';
import { translate } from '@/i18n';
import BracketsManagerAdapter from '@/services/pairings/BracketsManagerAdapter';

class PlayoffService {
  getGroups(tournament, ordered = []) {
    const settings = tournament.getEliminationSettings?.() || {};
    const topCount = Number(settings.participantsTop) || 0;
    const bottomCount = settings.bracketsType === 'top-bottom' ? Number(settings.participantsBottom) || 0 : 0;
    return Array.isArray(ordered)
      ? { topParticipants: ordered.slice(0, topCount), bottomParticipants: ordered.slice(topCount, topCount + bottomCount) }
      : { topParticipants: ordered?.topParticipants || [], bottomParticipants: bottomCount ? ordered?.bottomParticipants || [] : [] };
  }

  selectParticipants(tournament, standings = [], order = [], exclusions = []) {
    const rows = order.length ? order : standings;
    const groups = this.getGroups(tournament, rows);
    const active = tournament.getActiveMatchSides?.() || [];
    const select = (items, zone) => items
      .filter(row => !exclusions.some(entry => entry.id === row.id && entry.zone === zone))
      .map(row => active.find(side => side.id === row.id)).filter(Boolean);
    return {
      topParticipants: select(groups.topParticipants, 'top'),
      bottomParticipants: select(groups.bottomParticipants, 'bottom'),
    };
  }

  getGroupIssue(tournament, participants, zone = 'top') {
    const count = participants.length;
    const randomTeams = tournament.isTeamTournament() && tournament.teamSettings?.teamMode === TeamMode.RANDOM;
    const teamSize = randomTeams ? Number(tournament.getTeamSize()) || 2 : 1;
    const remainder = count % teamSize;
    const params = { count, teamSize, remainder, zone: translate(`playoffValidation.${zone}`) };
    if (randomTeams && remainder) return { code: 'INCOMPLETE_PLAYOFF_TEAMS', message: translate('playoffValidation.incompleteTeams', params) };
    if (count / teamSize < 2 || count / teamSize > 128) {
      return { code: 'INVALID_PLAYOFF_PARTICIPANT_COUNT', message: translate('playoffValidation.count', params) };
    }
    return null;
  }

  getIssues(tournament, groups) {
    const issues = [this.getGroupIssue(tournament, groups.topParticipants, 'top')];
    if (tournament.getEliminationSettings?.()?.bracketsType === 'top-bottom') {
      issues.push(this.getGroupIssue(tournament, groups.bottomParticipants, 'bottom'));
    }
    return issues.filter(Boolean);
  }

  formatIssues(issues) {
    const paragraphs = issues.map(issue => issue.message);
    if (issues.some(issue => issue.code === 'INCOMPLETE_PLAYOFF_TEAMS')) {
      paragraphs.push(translate('playoffValidation.incompleteTeamsHint'));
    }
    return paragraphs.join('\n\n');
  }

  assertGroups(tournament, groups) {
    const issues = this.getIssues(tournament, groups);
    if (issues.length) {
      const error = new Error(this.formatIssues(issues));
      error.code = 'PLAYOFF_VALIDATION';
      throw error;
    }
  }

  async pairSeeds(participants) {
    if (participants.length < 2 || participants.length > 128) throw new Error(translate('playoffValidation.count', { zone: '', count: participants.length }));
    return BracketsManagerAdapter.pairSeeds(participants);
  }
}

export default new PlayoffService();
